/**
 * selectors/symbolContext.ts
 *
 * Memoized symbol context selector for MER Algo Footprints v4.3+
 *
 * Problem solved:
 *   Before this: 7 files each independently run the full engine chain
 *   (gamma → zones → smc → regime → traps → setups) on every render.
 *   With a 10-symbol watchlist that is 10× the engine chain per tick.
 *
 *   After this: one cached result per symbol, invalidated only when
 *   candles actually change (new timestamp or count).
 *
 * Usage:
 *   const ctx = getSymbolContext(symbol, symbolData[symbol], settings, weights);
 *
 * All consumers — Dashboard, ThesisPanel, PostureBanner, WatchlistSidebar,
 * useAIOrchestration, panels — share the same computed result for the current tick.
 */

import { now } from '../../../utils/clock';
import { buildSymbolContext, SymbolContext } from '../../../services/buildSymbolContext';
import { AppSettings, ScoringWeights, DataState } from '../../../types';
// v8.0.0 — Attribution capture (additive, best-effort, no engine mutation)
import { captureFromContext } from '../../attribution/attributionRuntime';
// v9.2.0 -- Snapshot v2 client auto-capture (additive, best-effort, active-symbol only)
import { captureSnapshotV2FromContext } from '../../learning/snapshotV2Runtime';

// ─── Cache ────────────────────────────────────────────────────────────────────

interface CacheEntry {
  ctx:          SymbolContext;
  lastCandleTs: number;
  candleCount:  number;
  lastClose:    number;   // added to cache key for intra-bar price changes
  computedAt:   number;
}

// ─── Pure compute (no cache, no time dependency) ──────────────────────────────

export function computeSymbolContext(
  symbol:   string,
  data:     DataState | undefined,
  settings: AppSettings,
  weights:  ScoringWeights
): SymbolContext | null {
  if (!data?.candles?.length) return null;
  return buildSymbolContext(symbol, data, settings, weights);
}

const _cache   = new Map<string, CacheEntry>();
const MAX_AGE  = 30_000; // 30 s — force refresh even if candles unchanged

// ─── Main selector ────────────────────────────────────────────────────────────

export function getSymbolContext(
  symbol:   string,
  data:     DataState | undefined,
  settings: AppSettings,
  weights:  ScoringWeights
): SymbolContext | null {
  if (!data?.candles?.length) return null;

  const candles      = data.candles;
  const lastCandleTs = candles[candles.length - 1].ts;
  const candleCount  = candles.length;
  const lastClose    = candles[candles.length - 1].close;
  const cached       = _cache.get(symbol);

  if (
    cached &&
    cached.lastCandleTs === lastCandleTs &&
    cached.candleCount  === candleCount  &&
    cached.lastClose    === lastClose    &&
    (now() - cached.computedAt) < MAX_AGE
  ) {
    return cached.ctx;
  }

  const ctx = buildSymbolContext(symbol, data, settings, weights);
  if (!ctx) return null;

  _cache.set(symbol, { ctx, lastCandleTs, candleCount, lastClose: candles[candles.length-1].close, computedAt: now() });

  // v8.0.0 — Attribution capture at the cache-miss boundary (new state computed)
  // Best-effort: captureFromContext never throws, never mutates ctx
  captureFromContext(ctx as any);
  // v9.2.0 -- Snapshot v2 auto-capture (active-symbol only; server is the capture authority)
  captureSnapshotV2FromContext(ctx as any);

  return ctx;
}

export function invalidateSymbolContext(symbol: string): void {
  _cache.delete(symbol);
}

export function invalidateAllContexts(): void {
  _cache.clear();
}

/** Diagnostics helper */
export function getContextCacheStats(): { size: number; symbols: string[] } {
  return { size: _cache.size, symbols: Array.from(_cache.keys()) };
}
